import { unwrapConnection } from "@utils/utils";
import { useMemo } from "react";

import { ApiTreatmentPlanTemplateFieldTypeChoices, useTreatmentPlanTemplateQuery } from "@api/graphql/types-and-hooks";

/** Null domain (global fields) first, then by rankOrder within each group */
function sortByDomainThenRank<T extends { domain?: { id: string } | null; rankOrder: number }>(a: T, b: T): number {
  const aHasDomain = a.domain?.id != null;
  const bHasDomain = b.domain?.id != null;
  if (aHasDomain !== bHasDomain) return aHasDomain ? 1 : -1;
  return a.rankOrder - b.rankOrder;
}

/**
 * Fetches and returns sorted goal/target template fields for a report.
 * Uses the treatmentPlanTemplate query which is typically prefetched by ClinicalReportDetailsProvider.
 */
export function useTreatmentPlanTemplate(reportId: string | undefined) {
  const query = useTreatmentPlanTemplateQuery({ reportId: reportId || "" }, { enabled: !!reportId, retry: false });

  const isGasEnabled = query.data?.treatmentPlanTemplate?.isGasEnabled ?? false;
  const isMedicalNecessityEnabled = query.data?.treatmentPlanTemplate?.isMedicalNecessityEnabled ?? false;

  const allFields = useMemo(
    () => (unwrapConnection(query.data?.treatmentPlanTemplate?.templateFields) ?? []).filter(Boolean),
    [query.data],
  );

  const domainFields = useMemo(
    () =>
      [...allFields]
        .filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Domain && f.isEnabled)
        .sort(sortByDomainThenRank),
    [allFields],
  );

  const goalFields = useMemo(
    () =>
      [...allFields]
        .filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Goal && f.isEnabled)
        .sort(sortByDomainThenRank),
    [allFields],
  );

  const targetFields = useMemo(
    () =>
      [...allFields]
        .filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Target && f.isEnabled)
        .sort(sortByDomainThenRank),
    [allFields],
  );

  return {
    domainFields,
    goalFields,
    targetFields,
    isLoading: query.isLoading,
    isGasEnabled,
    isMedicalNecessityEnabled,
  };
}
